
import { useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppRole } from '@/types/roles';
import { ROUTES } from '@/constants/routes';
import PageLoader from '@/components/PageLoader';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredRole?: AppRole;
  requiredRoles?: AppRole[];
  redirectTo?: string;
  showToast?: boolean;
}

export const AuthGuard = ({ 
  children, 
  requireAuth = true,
  requiredRole,
  requiredRoles,
  redirectTo = ROUTES.LOGIN,
  showToast = true
}: AuthGuardProps) => {
  if (requiredRole && requiredRoles) {
    throw new Error('AuthGuard: Use either requiredRole or requiredRoles, not both.');
  }

  const { user, isLoading, currentRole, hasRole, hasAnyRole, rolesLoaded } = useAuth();

  // Derive access denial reason (render-safe, no side effects)
  const denialReason = useMemo(() => {
    if (isLoading) return null;
    if (requireAuth && !user) return 'auth_required';
    if (user && requiredRole && !hasRole(requiredRole)) return 'role_denied';
    if (user && requiredRoles && !hasAnyRole(requiredRoles)) return 'roles_denied';
    return null;
  }, [isLoading, user, rolesLoaded, requireAuth, requiredRole, requiredRoles, hasRole, hasAnyRole]);

  // Pre-compute buyer status to avoid unstable hasRole in effect deps
  const isBuyerUser = !!user && hasRole('buyer');

  // Fire toasts in useEffect only, never during render
  useEffect(() => {
    if (!showToast || !denialReason) return;
    if (denialReason === 'auth_required') {
      // Intentionally no toast — the login page is self-explanatory and
      // showing a toast here can leak across navigations (e.g. landing on "/").
    } else if (denialReason === 'role_denied' && requiredRole) {
      if (!(requiredRole === 'seller' && isBuyerUser)) {
        toast.error(`Access denied. ${requiredRole} role required.`);
      }
    } else if (denialReason === 'roles_denied' && requiredRoles) {
      if (!(requiredRoles.includes('seller') && isBuyerUser)) {
        toast.error(`Access denied. Required roles: ${requiredRoles.join(' or ')}`);
      }
    }
  }, [denialReason, showToast, requiredRole, requiredRoles, isBuyerUser]);

  // Unified render logic driven by denialReason
  if (isLoading || (user && (requiredRole || requiredRoles) && !rolesLoaded)) {
    return <PageLoader />;
  }

  if (denialReason === 'auth_required') {
    return <Navigate to={redirectTo} replace />;
  }

  if (denialReason === 'role_denied' || denialReason === 'roles_denied') {
    const needsUpgrade =
      (requiredRole === 'seller' || requiredRoles?.includes('seller')) && hasRole('buyer');
    return <Navigate to={needsUpgrade ? ROUTES.BUYER_UPGRADE : ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

// Convenience components for specific role protection
export const AdminGuard = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard requiredRole="admin">{children}</AuthGuard>
);

export const SellerGuard = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard requiredRole="seller">{children}</AuthGuard>
);

export const BuyerGuard = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard requiredRole="buyer">{children}</AuthGuard>
);

export const SellerOrAdminGuard = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard requiredRoles={['seller', 'admin']}>{children}</AuthGuard>
);
